import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import Choices from 'choices.js';
import 'choices.js/public/assets/styles/choices.min.css';
import MyPage from '../../MyPage';
import { useSatellite } from '../../context/SatelliteContext';
import { getSatelliteApiParam, displaySatellite } from '../../helpers/satellite';
import SubTabsWarehouses from '../../SubTabsWarehouses';
import axiosService from '../../helpers/axios';
import { Blocks } from 'react-loader-spinner';
import './WarehousesStockReport.css';

const PAGE_SIZE = 100;
const ROW_HEIGHT = 45;
const STOCK_REPORT_BASE = '/stockreport';
const REPORT_PATHS = { all: '', negative: 'negative', zero: 'zero' };

const API_FILTER_KEYS = ['year', 'warehouse', 'sapcode', 'product', 'itemtype', 'satellite'];

const formatNumber = (value) => {
  if (value == null || value === '') return '';
  if (typeof value !== 'number') return value;
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(value);
};

function MonthCellWithAlert(params) {
  const valueField = params.colDef?.field || '';
  const alertField = valueField.replace('_o', '_leadtime_alert');
  const alert = params.data?.[alertField];
  const value = formatNumber(params.value);
  return (
    <div className="stock-report-month-cell">
      <span className="stock-report-month-value">{value}</span>
      {alert != null && String(alert).trim() !== '' && (
        <span className="stock-report-cell-alert">{String(alert)}</span>
      )}
    </div>
  );
}

const monthCol = (field, headerName, width = 120) => ({
  field,
  headerName,
  width,
  minWidth: 100,
  filter: false,
  floatingFilter: false,
  cellClass: 'ag-numeric-cell',
  valueFormatter: (p) => formatNumber(p.value),
  cellRenderer: MonthCellWithAlert,
  cellClassRules: {
    'stock-report-cell-negative': (p) => typeof p.value === 'number' && p.value < 0,
    'stock-report-cell-zero': (p) => p.value === 0,
    'stock-report-cell-high': (p) => typeof p.value === 'number' && p.value >= 1500,
  },
});

const initialApiFilters = {
  year: '',
  warehouse: '',
  sapcode: '',
  product: '',
  itemtype: '',
  satellite: '',
};

export default function WarehousesStockReport() {
  const { satellite: globalSatellite } = useSatellite();
  const [quickFilterText, setQuickFilterText] = useState('');
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [apiFilters, setApiFilters] = useState(initialApiFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [reportMode, setReportMode] = useState('all'); // 'all' | 'negative' | 'zero'
  const [sapcodeOptions, setSapcodeOptions] = useState([]);
  const sapcodeSelectRef = useRef(null);
  const sapcodeChoicesRef = useRef(null);

  const fetchPage = useCallback((page) => {
    const path = REPORT_PATHS[reportMode];
    const url = path ? `${STOCK_REPORT_BASE}/${path}/` : `${STOCK_REPORT_BASE}/`;
    const params = { page, page_size: PAGE_SIZE };
    API_FILTER_KEYS.forEach((key) => {
      const value = apiFilters[key]?.trim();
      if (value) params[key] = value;
    });
    const satGlobal = getSatelliteApiParam(globalSatellite);
    if (satGlobal && !params.satellite) params.satellite = satGlobal;
    setLoading(true);
    axiosService
      .get(url, { params })
      .then((res) => {
        const { count, results } = res.data;
        setTotalCount(count);
        setRowData(results || []);
      })
      .catch((err) => {
        console.error('Stock report fetch error:', err);
        setRowData([]);
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
  }, [apiFilters, reportMode, globalSatellite]);

  useEffect(() => {
    fetchPage(currentPage);
  }, [currentPage, fetchPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [apiFilters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [reportMode]);

  const columnDefs = useMemo(
    () => [
      { field: 'fyname_o', headerName: 'FY', width: 90, pinned: 'left' },
      { field: 'warehousename_o', headerName: 'Warehouse', width: 170, pinned: 'left' },
      { field: 'sapcode_o', headerName: 'SAP Code', width: 110, pinned: 'left' },
      {
        field: 'satellite',
        headerName: 'Satellite',
        width: 100,
        pinned: 'left',
        valueFormatter: (p) => displaySatellite(p.value, { csv: true }),
      },
      { field: 'productdescription_o', headerName: 'Product Description', width: 220, pinned: 'left'  },
      { field: 'itemtypename_o', headerName: 'Item Type', width: 130 },
      { field: 'regionname_o', headerName: 'Region', width: 140},
      monthCol('july_o', 'July'),
      monthCol('august_o', 'August'),
      monthCol('september_o', 'September', 120),
      monthCol('october_o', 'October', 115),
      monthCol('november_o', 'November', 120),
      monthCol('december_o', 'December', 120),
      monthCol('january_o', 'January', 115),
      monthCol('february_o', 'February', 120),
      monthCol('march_o', 'March'),
      monthCol('april_o', 'April'),
      monthCol('may_o', 'May'),
      monthCol('june_o', 'June'),
    ],
    []
  );

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: true,
    floatingFilter: true,
    suppressMovable: false,
  }), []);

  const totalPages = totalCount != null ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : 0;
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const setApiFilter = useCallback((key, value) => {
    setApiFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    axiosService
      .get(`${STOCK_REPORT_BASE}/sapcode/`)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) setSapcodeOptions(list.map(String));
      })
      .catch((err) => {
        console.error('SAP code list fetch error:', err);
        if (!cancelled) setSapcodeOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const selectEl = sapcodeSelectRef.current;
    if (!selectEl || sapcodeChoicesRef.current) return;

    const choices = new Choices(selectEl, {
      removeItemButton: true,
      shouldSort: false,
      searchEnabled: true,
      searchResultLimit: 50,
      itemSelectText: '',
      placeholder: true,
      placeholderValue: 'Select SAP code(s)…',
    });
    sapcodeChoicesRef.current = choices;

    const handleChange = () => {
      const values = Array.from(selectEl.selectedOptions || [])
        .map((o) => String(o.value))
        .filter((v) => v.trim() !== '');
      setApiFilter('sapcode', values.join(','));
    };

    selectEl.addEventListener('change', handleChange);
    return () => {
      selectEl.removeEventListener('change', handleChange);
      try {
        choices.destroy();
      } catch (e) {
        // ignore
      }
      sapcodeChoicesRef.current = null;
    };
  }, [setApiFilter]);

  useEffect(() => {
    const choices = sapcodeChoicesRef.current;
    if (!choices) return;

    const selected = String(apiFilters.sapcode || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const items = sapcodeOptions.map((v) => ({
      value: v,
      label: v,
      disabled: false,
    }));

    choices.clearChoices();
    choices.setChoices(items, 'value', 'label', true);
    choices.removeActiveItems();
    if (selected.length) choices.setChoiceByValue(selected);
  }, [sapcodeOptions, apiFilters.sapcode]);

  return (
    <div className="scrollable-container">
      <MyPage />
      <SubTabsWarehouses />

      <div className="stock-report-toolbar">
        <div className="stock-report-title-row">
          <div className="stock-report-title">
            Stock report - Shortage
            {totalCount != null && (
              <span className="stock-report-count"> ({totalCount.toLocaleString()} rows)</span>
            )}
          </div>
          <div className="stock-report-mode-buttons">
            <button
              type="button"
              className={`btn btn-sm ${reportMode === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setReportMode('all')}
            >
              All
            </button>
            <button
              type="button"
              className={`btn btn-sm ${reportMode === 'negative' ? 'btn-danger' : 'btn-outline-danger'}`}
              onClick={() => setReportMode('negative')}
            >
              Negative
            </button>
            <button
              type="button"
              className={`btn btn-sm ${reportMode === 'zero' ? 'btn-warning' : 'btn-outline-warning'}`}
              onClick={() => setReportMode('zero')}
            >
              Zero
            </button>
          </div>
        </div>
        <div className="stock-report-legend">
          <span className="stock-report-legend-item">
            <span className="stock-report-dot stock-report-dot-negative" /> Negative
          </span>
          <span className="stock-report-legend-item">
            <span className="stock-report-dot stock-report-dot-zero" /> Zero
          </span>
          <span className="stock-report-legend-item">
            <span className="stock-report-dot stock-report-dot-high" /> High
          </span>
        </div>
        <input
          className="stock-report-search form-control"
          placeholder="Search in grid..."
          value={quickFilterText}
          onChange={(e) => setQuickFilterText(e.target.value)}
        />
      </div>

      <div className="stock-report-filters">
        <div className="stock-report-filter-group">
          <label className="stock-report-filter-label">Year</label>
          <input
            type="text"
            className="form-control stock-report-filter-input"
            placeholder="Filter by year…"
            value={apiFilters.year}
            onChange={(e) => setApiFilter('year', e.target.value)}
          />
        </div>
        <div className="stock-report-filter-group">
          <label className="stock-report-filter-label">Warehouse</label>
          <input
            type="text"
            className="form-control stock-report-filter-input"
            placeholder="Filter by warehouse…"
            value={apiFilters.warehouse}
            onChange={(e) => setApiFilter('warehouse', e.target.value)}
          />
        </div>
        <div className="stock-report-filter-group">
          <label className="stock-report-filter-label">SAP Code</label>
          <select
            ref={sapcodeSelectRef}
            className="form-control stock-report-filter-input stock-report-sapcode-select"
            multiple
            placeholder="Select SAP code(s)…"
            aria-label="Filter by SAP code(s)"
          >
          </select>
        </div>
        <div className="stock-report-filter-group">
          <label className="stock-report-filter-label">Product</label>
          <input
            type="text"
            className="form-control stock-report-filter-input"
            placeholder="Filter by product…"
            value={apiFilters.product}
            onChange={(e) => setApiFilter('product', e.target.value)}
          />
        </div>
        <div className="stock-report-filter-group">
          <label className="stock-report-filter-label">Item type</label>
          <input
            type="text"
            className="form-control stock-report-filter-input"
            placeholder="Filter by item type…"
            value={apiFilters.itemtype}
            onChange={(e) => setApiFilter('itemtype', e.target.value)}
          />
        </div>
        <div className="stock-report-filter-group">
          <label className="stock-report-filter-label">Satellite</label>
          <input
            type="text"
            className="form-control stock-report-filter-input"
            placeholder="Filter by satellite…"
            value={apiFilters.satellite}
            onChange={(e) => setApiFilter('satellite', e.target.value)}
          />
        </div>
        <div className="stock-report-filter-actions">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setApiFilters(initialApiFilters)}
          >
            Clear filters
          </button>
        </div>
      </div>

      {loading && (
        <div className="stock-report-loading">
          <Blocks visible height="48" width="48" ariaLabel="loading" />
          <span>Loading…</span>
        </div>
      )}

      <div className="ag-theme-quartz stock-report-grid">
        <AgGridReact
          rowData={rowData}
          rowHeight={ROW_HEIGHT}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          quickFilterText={quickFilterText}
          animateRows={true}
          tooltipShowDelay={0}
          getRowId={(p) => String(p.data?.stockviewid ?? '')}
        />
      </div>

      {totalPages > 0 && (
        <div className="stock-report-pagination">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!canPrev}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className="stock-report-pagination-info">
            Page {currentPage} of {totalPages}
            {totalCount != null && (
              <> ({totalCount.toLocaleString()} total)</>
            )}
          </span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!canNext}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
